import { TFunction } from 'i18next'
import { z } from 'zod'

import { PaymentSettings, ProcessingFee, ProcessingFeeType } from '@/api/building'
import { PmsProvider } from '@/api/pms'
import { Building } from '@/reducers/types'
import { transformCentsToDollars, transformDollarsToCents } from '@/utils/currency'

export const PAY_RENT_FEE_MAX_PERCENTAGE = 100
export const PAY_RENT_MAX_PROCESSING_DELAY = 90

export const PAY_RENT_FEE_TYPES: ProcessingFeeType[] = ['card', 'ach']

export const PAY_RENT_FEE_FIELDS = {
  ach: { flatAmount: 'achFeeFlatAmount', percentage: 'achFeePercentage' },
  card: { flatAmount: 'cardFeeFlatAmount', percentage: 'cardFeePercentage' },
} as const

export type PayRentFeeFieldName =
  (typeof PAY_RENT_FEE_FIELDS)[ProcessingFeeType][keyof (typeof PAY_RENT_FEE_FIELDS)['card']]

// Note: the backend only reads payment settings for a Yardi building with a payment id or a Rent Manager one
export const isPayRentAvailable = (building: Building, provider: PmsProvider) =>
  (provider === 'yardi' && !!building.yardiPaymentId) || provider === 'rentManager'

export const makePayRentSchema = (t: TFunction<['organisation']>, isRentManager: boolean) => {
  const percentage = z.string().refine(
    (value) => !value || Number(value) <= PAY_RENT_FEE_MAX_PERCENTAGE,
    t('organisation:places.integrations.payRentConfig.validation.feeMax', {
      max: PAY_RENT_FEE_MAX_PERCENTAGE,
    }),
  )
  const chargeCode = (message: string) =>
    isRentManager
      ? z.string().nullable()
      : z
          .string()
          .nullable()
          .refine((value) => !!value, message)

  return z
    .object({
      achFeeFlatAmount: z.string(),
      achFeePercentage: percentage,
      allowAutopay: z.boolean(),
      allowChargesSelection: z.boolean(),
      allowPartialPayment: z.boolean(),
      amenityChargeCode: chargeCode(
        t('organisation:places.integrations.payRentConfig.validation.amenityChargeCodeRequired'),
      ),
      cardFeeFlatAmount: z.string(),
      cardFeePercentage: percentage,
      chargeCodesMappings: z.array(
        z.object({
          code: z.string(),
          description: z
            .string()
            .trim()
            .min(
              1,
              t('organisation:places.integrations.payRentConfig.validation.descriptionRequired'),
            ),
        }),
      ),
      dailyBatch: z.boolean(),
      prePaidChargeCode: chargeCode(
        t('organisation:places.integrations.payRentConfig.validation.prePaidChargeCodeRequired'),
      ),
      processingDelay: z
        .string()
        .min(
          1,
          t('organisation:places.integrations.payRentConfig.validation.processingDelayRequired'),
        ),
    })
    .superRefine((data, ctx) => {
      PAY_RENT_FEE_TYPES.forEach((type) => {
        const fields = PAY_RENT_FEE_FIELDS[type]
        if (data[fields.percentage] || data[fields.flatAmount]) return

        ctx.addIssue({
          code: 'custom',
          message: t('organisation:places.integrations.payRentConfig.validation.feeRequired'),
          path: [fields.percentage],
        })
      })
    })
}

export type PayRentFormData = z.infer<ReturnType<typeof makePayRentSchema>>

// Note: a fee is either a percentage or a flat amount - the filled input decides the mode
const getFeeFormValues = (fees: ProcessingFee[], type: ProcessingFeeType) => {
  const fee = fees.find((item) => item.type === type)

  if (fee?.mode === 'flat') {
    return { flatAmount: String(transformCentsToDollars(fee.flatAmount ?? 0)), percentage: '' }
  }

  return { flatAmount: '', percentage: String(fee?.percentage ?? 0) }
}

export const getPayRentDefaults = ({
  chargeCodesMappings = [],
  processingFees = [],
  settings = {},
}: PaymentSettings): PayRentFormData => {
  const card = getFeeFormValues(processingFees, 'card')
  const ach = getFeeFormValues(processingFees, 'ach')

  return {
    achFeeFlatAmount: ach.flatAmount,
    achFeePercentage: ach.percentage,
    allowAutopay: !!settings.allowAutopay,
    allowChargesSelection: !!settings.allowChargesSelection,
    allowPartialPayment: !!settings.allowPartialPayment,
    amenityChargeCode: settings.amenityChargeCode ?? null,
    cardFeeFlatAmount: card.flatAmount,
    cardFeePercentage: card.percentage,
    chargeCodesMappings: chargeCodesMappings.map(({ code, description }) => ({
      code,
      description: description ?? '',
    })),
    dailyBatch: !!settings.dailyBatch,
    prePaidChargeCode: settings.prePaidChargeCode ?? null,
    processingDelay: String(settings.processingDelay ?? 0),
  }
}

const toProcessingFee = (
  type: ProcessingFeeType,
  percentage: string,
  flatAmount: string,
): ProcessingFee =>
  flatAmount
    ? { flatAmount: transformDollarsToCents(flatAmount), mode: 'flat', type }
    : { mode: 'percent', percentage: Number(percentage), type }

export const getPayRentPayload = (
  values: PayRentFormData,
  server: PaymentSettings,
): PaymentSettings => ({
  chargeCodesMappings: values.chargeCodesMappings.map(({ code, description }) => ({
    code,
    description: description.trim(),
  })),
  processingFees: PAY_RENT_FEE_TYPES.map((type) => {
    const fields = PAY_RENT_FEE_FIELDS[type]
    return toProcessingFee(type, values[fields.percentage], values[fields.flatAmount])
  }),
  settings: {
    allowAutopay: values.allowAutopay,
    allowChargesSelection: values.allowChargesSelection,
    allowPartialPayment: values.allowPartialPayment,
    amenityChargeCode: values.amenityChargeCode,
    dailyBatch: values.dailyBatch,
    prePaidChargeCode: values.prePaidChargeCode,
    processingDelay: Number(values.processingDelay),
    syncAmenityCharges: !!server.settings?.syncAmenityCharges,
  },
})
